import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { CountryCode } from 'models/Country';
import { Lead, LeadStatus } from 'models/Leads';
import { LeadV3Source } from 'models/LeadsV3';
import { PinCode } from 'models/PinCode';
import { ReviewByHost } from 'models/Reviews';
import { Tag } from 'models/Tags';
import { getCountryCode } from 'utils/languageUtils';
import {
  getCurrentAttributedChannel,
  getLeadArrivalDate,
  getLeadCheckInDate,
  getLeadCheckOutDate,
  getLeadDepartureDate,
} from 'utils/lead/leadUtils';
import { setHours } from 'date-fns';
import { HasOrderItems } from 'models/hasOrderItems/HasOrderItems';
import { Property } from 'models/Properties';
import { CreateLeadModalParams } from './LeadModal.types';
import LeadModalContext from './LeadModalContext';

export const findPropertyAvailability = (
  properties: Property[],
  uid: string,
) => {
  // Check if it's a main property
  const mainProperty = properties?.find((p) => p.uid === uid);
  if (mainProperty)
    return {
      checkInTimeStart: mainProperty.availability?.checkInTimeStart ?? -1,
      checkOutTime: mainProperty.availability?.checkOutTime ?? -1,
    };

  // Check if it's a subUnit
  const propertyWithSubUnit = properties?.find(
    (p) =>
      p.subUnits?.length > 0 &&
      p.subUnits.some((subUnit) => subUnit.uid === uid),
  );

  if (propertyWithSubUnit && propertyWithSubUnit.subUnits) {
    const foundSubUnit = propertyWithSubUnit.subUnits.find(
      (subUnit) => subUnit.uid === uid,
    );

    if (foundSubUnit)
      return {
        checkInTimeStart: foundSubUnit.availability?.checkInTimeStart ?? -1,
        checkOutTime: foundSubUnit.availability?.checkOutTime ?? -1,
      };
  }

  const propertyWithUnitType = properties?.find(
    (p) =>
      p.unitTypes &&
      p.unitTypes.length > 0 &&
      (p.unitTypes.some((unitType) => unitType.uid === uid) ||
        p.unitTypes.some(
          (unitType) =>
            unitType.units &&
            unitType.units.length > 0 &&
            unitType.units.some((unit) => unit.uid === uid),
        )),
  );

  if (propertyWithUnitType && propertyWithUnitType.unitTypes) {
    // Check if it's a unitType
    const foundUnitType = propertyWithUnitType.unitTypes.find(
      (unitType) => unitType.uid === uid,
    );
    if (foundUnitType)
      return {
        checkInTimeStart: foundUnitType.availability?.checkInTimeStart ?? -1,
        checkOutTime: foundUnitType.availability?.checkOutTime ?? -1,
      };

    // Check if it's a unit and return the unitType availability
    const parentUnitType = propertyWithUnitType.unitTypes.find(
      (unitType) =>
        unitType.units &&
        unitType.units.length > 0 &&
        unitType.units.some((unit) => unit.uid === uid),
    );
    return {
      checkInTimeStart: parentUnitType?.availability?.checkInTimeStart ?? -1,
      checkOutTime: parentUnitType?.availability?.checkOutTime ?? -1,
    };
  }

  return {
    checkInTimeStart: -1,
    checkOutTime: -1,
  };
};

const useLeadModalFormInitialValue = () => {
  const {
    i18n: { language },
  } = useTranslation();
  const { availableProperties } = useContext(LeadModalContext);

  return ({
    lead,
    leadForRebooking,
    leadTags,
    params,
    pinCode,
    reviewByHost,
    defaultPropertyUid,
    mainBookingAssigneeUid,
  }: {
    lead: Lead | undefined;
    leadForRebooking: Lead | null;
    leadTags: Tag[] | undefined;
    params: CreateLeadModalParams;
    pinCode: PinCode | undefined;
    reviewByHost: ReviewByHost | undefined;
    defaultPropertyUid: string | undefined;
    quote: HasOrderItems;
    mainBookingAssigneeUid: string | undefined;
  }) => {
    if (lead) {
      const {
        address,
        address2,
        adultCount,
        assignee,
        cellPhoneNumber,
        childrenCount,
        city,
        countryCode,
        creationMetadata,
        customData,
        email,
        externalBookingID,
        extraNotes,
        firstName,
        flightNumber,
        guest,
        infantCount,
        lastName,
        notes,
        petCount,
        phoneNumber,
        preferredLocale,
        property: { uid: propertyUid },
        reasonForTrip,
        reviewByHost: leadReviewByHost,
        secondaryEmail,
        source,
        state,
        status,
        uid,
        zipCode,
        order,
        unitUid,
      } = lead;
      const checkInDate = getLeadCheckInDate(lead);
      const checkOutDate = getLeadCheckOutDate(lead);
      const arrivalDate = getLeadArrivalDate(lead);
      const departureDate = getLeadDepartureDate(lead);

      return {
        address,
        address2,
        adultCount,
        arrivalDate,
        assigneeUid: assignee?.uid || mainBookingAssigneeUid,
        attributedChannel: getCurrentAttributedChannel(lead) ?? '',
        cellPhoneNumber,
        childrenCount,
        checkInDate,
        checkOutDate,
        city,
        countryCode: countryCode || getCountryCode(language) || CountryCode.US,
        departureDate,
        email,
        extraNotes,
        firstName,
        flightNumber,
        infantCount,
        lastName,
        notes,
        phoneNumber,
        petCount,
        pinCode,
        preferredLocale,
        propertyUid,
        reasonForTrip,
        reviewByHost: reviewByHost || leadReviewByHost,
        secondaryEmail,
        source,
        state,
        status,
        tags: leadTags,
        customData,
        uid,
        zipCode,
        guest,
        externalBookingID,
        creationMetadata,
        force100PercentPaymentAtReservation:
          order?.force100PercentPaymentAtReservation,
        unitUid,
        order,
        quote: null,
        currentGroupBookingLeadUid: lead.uid,
      };
    }

    const { dateFrom, dateTo, propertyUid } = params;
    const {
      address,
      address2,
      adultCount,
      cellPhoneNumber,
      childrenCount,
      city,
      countryCode,
      email,
      extraNotes,
      firstName,
      flightNumber,
      infantCount,
      lastName,
      notes,
      petCount,
      phoneNumber,
      preferredLocale,
      property,
      reasonForTrip,
      secondaryEmail,
      state,
      zipCode,
    } = leadForRebooking || {};

    const selectedPropertyUid =
      property?.uid || propertyUid || defaultPropertyUid;

    const { checkInTimeStart, checkOutTime } = findPropertyAvailability(
      availableProperties,
      selectedPropertyUid as string,
    );

    const arrivalDate =
      checkInTimeStart > -1 ? setHours(dateFrom, checkInTimeStart) : dateFrom;
    const departureDate =
      checkOutTime > -1 ? setHours(dateTo, checkOutTime) : dateTo;

    return {
      adultCount: adultCount || 1,
      arrivalDate,
      checkInDate: dateFrom,
      checkOutDate: dateTo,
      childrenCount: childrenCount || 0,
      countryCode: countryCode || getCountryCode(language) || CountryCode.US,
      departureDate,
      infantCount: infantCount || 0,
      petCount: petCount || 0,
      propertyUid: selectedPropertyUid,
      source: LeadV3Source.HOSTFULLY_UI,
      status: leadForRebooking
        ? LeadStatus.BOOKED_BY_AGENT
        : LeadStatus.ON_HOLD,
      address,
      address2,
      cellPhoneNumber,
      city,
      email,
      extraNotes,
      firstName,
      flightNumber,
      lastName,
      notes,
      phoneNumber,
      preferredLocale,
      reasonForTrip,
      secondaryEmail,
      state,
      zipCode,
    };
  };
};

export default useLeadModalFormInitialValue;
